import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/shared/lib/axios";
import { clinicApiPath } from "@/shared/lib/apiPath";
import { useClinicContext } from "./useClinicContext";

// ==================== Types ====================

export interface Patient {
  id: number;
  clinic_id: number;
  name: string;
  name_kana?: string;
  birth_date?: string;
  sex?: number;
  phone?: string;
  address?: string;
  postal_code?: string;
  insurance_number?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  care_level?: string;
  // Optional API fields (patient detail / list responses)
  serial_number?: number;
  current_patient_information?: { name: string };
  patient_informations?: { name: string }[];
}

export interface PatientListParams {
  page?: number;
  perPage?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export interface PaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number;
  to: number;
}

export interface PatientListResponse {
  data: Patient[];
  pagination: PaginationMeta;
}

// ==================== API Service Functions ====================

/**
 * Get list of patients
 */
const getPatients = async (
  siteType: string,
  clinicNumber: string,
  clinicId?: number,
  params?: PatientListParams,
): Promise<PatientListResponse> => {
  const response = await apiClient.get(
    clinicApiPath(siteType, clinicNumber, '/patients'),
    {
    params: {
      clinic_id: clinicId,
      ...params,
    },
  });
  return response.data.data;
};

/**
 * Get single patient by ID
 */
const getPatient = async (siteType: string, clinicNumber: string, id: number): Promise<Patient> => {
  const response = await apiClient.get(
    clinicApiPath(siteType, clinicNumber, `/patients/${id}`)
  );
  return response.data.data;
};

/**
 * Create new patient
 */
const createPatient = async (
  siteType: string,
  clinicNumber: string,
  data: Partial<Patient>,
): Promise<Patient> => {
  const response = await apiClient.post(
    clinicApiPath(siteType, clinicNumber, '/patients'),
    data
  );
  return response.data.data;
};

/**
 * Update existing patient
 */
const updatePatient = async (
  siteType: string,
  clinicNumber: string,
  id: number,
  data: Partial<Patient>,
): Promise<Patient> => {
  const response = await apiClient.put(
    clinicApiPath(siteType, clinicNumber, `/patients/${id}`),
    data
  );
  return response.data.data;
};

/**
 * Delete patient
 */
const deletePatient = async (siteType: string, clinicNumber: string, id: number): Promise<void> => {
  await apiClient.delete(
    clinicApiPath(siteType, clinicNumber, `/patients/${id}`)
  );
};

/**
 * Search patients
 */
const searchPatients = async (
  siteType: string,
  clinicNumber: string,
  clinicId: number,
  keyword: string,
): Promise<Patient[]> => {
  const response = await apiClient.get(
    clinicApiPath(siteType, clinicNumber, '/patients/search'),
    {
    params: {
      clinic_id: clinicId,
      keyword,
    },
  });
  return response.data.data;
};

// ==================== Hooks ====================

/**
 * Hook to get a single patient by ID
 */
export const useMainPatient = (id?: number) => {
  const { tenant, clinicNumber } = useClinicContext();

  return useQuery({
    queryKey: ["patients", tenant, clinicNumber, id],
    queryFn: () => getPatient(tenant, clinicNumber, id!),
    enabled: !!id && !!tenant && !!clinicNumber,
  });
};

/**
 * Hook to get list of patients
 */
export const useMainPatients = (
  clinicId?: number,
  params?: PatientListParams,
) => {
  const { tenant, clinicNumber } = useClinicContext();

  return useQuery({
    queryKey: ["patients", tenant, clinicNumber, "list", clinicId, params],
    queryFn: () => getPatients(tenant, clinicNumber, clinicId, params),
    enabled: !!tenant && !!clinicNumber,
  });
};

/**
 * Hook to search patients
 */
export const useMainSearchPatients = (clinicId: number, keyword: string) => {
  const { tenant, clinicNumber } = useClinicContext();

  return useQuery({
    queryKey: ["patients", tenant, clinicNumber, "search", clinicId, keyword],
    queryFn: () => searchPatients(tenant, clinicNumber, clinicId, keyword),
    enabled: !!tenant && !!clinicNumber && !!clinicId && !!keyword,
  });
};

/**
 * Hook for patient mutations (create, update, delete)
 */
export const useMainPatientMutation = () => {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();

  const createPatientMutation = useMutation({
    mutationFn: (data: Partial<Patient>) => createPatient(tenant, clinicNumber, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patients", tenant, clinicNumber] });
    },
  });

  const updatePatientMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Patient> }) =>
      updatePatient(tenant, clinicNumber, id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["patients", tenant, clinicNumber] });
      queryClient.invalidateQueries({
        queryKey: ["patients", tenant, clinicNumber, variables.id],
      });
    },
  });

  const deletePatientMutation = useMutation({
    mutationFn: (id: number) => deletePatient(tenant, clinicNumber, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patients", tenant, clinicNumber] });
    },
  });

  return {
    createPatient: createPatientMutation,
    updatePatient: updatePatientMutation,
    deletePatient: deletePatientMutation,
  };
};
